import React from 'react';
import { Link } from 'react-router-dom';
import {
  IconBell, IconCalendar, IconClock, IconDental, IconPlus, IconSearch, IconMapPin, IconHistory, IconChevronRight,
} from '@tabler/icons-react';
import { Avatar, Button, Skeleton, StatusBadge } from '../../components/common';
import AppointmentRow from '../../components/booking/AppointmentRow';
import appointmentApi from '../../api/appointment/appointmentApi';
import notificationApi from '../../api/notification/notificationApi';
import useFetch from '../../hooks/useFetch';
import { useAuth } from '../../context/AuthContext';
import { useClinic } from '../../context/ClinicContext';
import { openDirections } from '../../services/mapsService';
import { PATHS, toPath } from '../../routes/paths';
import { formatDayDate, formatShortDate, formatTime, greeting } from '../../utils/date';

const QUICK_LINKS = [
  { to: PATHS.doctors, label: 'Doctors', Icon: IconSearch },
  { to: PATHS.clinic, label: 'Clinic info', Icon: IconMapPin },
  { to: PATHS.appointments, label: 'Appointments', Icon: IconCalendar },
  { to: PATHS.history, label: 'History', Icon: IconHistory },
];

function NextAppointment({ appointment: a, clinic }) {
  const subtitle = [a.doctor?.specialty, a.visitType].filter(Boolean).join(' · ');
  return (
    <section className="next-appt">
      <IconDental className="next-appt__art" size={72} stroke={1.1} aria-hidden="true" />
      <div className="next-appt__head">
        <span className="next-appt__eyebrow">Next appointment</span>
        <StatusBadge status={a.status} short className="badge--white" />
      </div>
      <Link to={toPath(PATHS.appointmentDetail, { id: a.id })} className="next-appt__doctor">
        <Avatar name={a.doctor?.name} tone="on-primary" />
        <div>
          <p className="next-appt__name">{a.doctor?.name}</p>
          <p className="next-appt__meta">{subtitle}</p>
        </div>
      </Link>
      <div className="next-appt__when">
        <span><IconCalendar size={18} />{formatDayDate(a.date)}</span>
        <span><IconClock size={18} />{formatTime(a.time)}</span>
      </div>
      <div className="next-appt__actions">
        <Button variant="on-primary" size="sm" to={toPath(PATHS.reschedule, { id: a.id })}>Reschedule</Button>
        <Button variant="on-primary-outline" size="sm" onClick={() => openDirections(clinic)}>Get directions</Button>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const upcoming = useFetch(() => appointmentApi.list('upcoming'), [], { initial: [] });
  const past = useFetch(() => appointmentApi.list('past'), [], { initial: [] });
  const notifications = useFetch(() => notificationApi.list(), [], { initial: [] });

  const [next, ...rest] = upcoming.data;
  const unread = notifications.data.some((n) => !n.read);

  return (
    <div className="page page--with-nav home">
      <header className="home__header">
        <Link to={PATHS.profile}><Avatar name={user?.name} size="sm" /></Link>
        <div className="u-grow">
          <p className="home__greeting">{`${greeting()},`}</p>
          <p className="home__name">{user?.preferredName || user?.name}</p>
        </div>
        <Link to={PATHS.notifications} className="icon-btn icon-btn--boxed home__bell" aria-label="Notifications">
          <IconBell size={22} />
          {unread && <i className="home__bell-dot" />}
        </Link>
      </header>

      <div className="page__body home__body">
        {upcoming.loading && !upcoming.data.length && <Skeleton height={260} />}
        {next && <NextAppointment appointment={next} clinic={clinic} />}

        <Button variant="dark" block to={PATHS.doctors} className="home__book">
          <IconPlus size={20} stroke={2.4} />
          Book an appointment
        </Button>

        <nav className="quick-links">
          {QUICK_LINKS.map(({ to, label, Icon }) => (
            <Link key={label} to={to} className="quick-links__item">
              <span className="quick-links__icon"><Icon size={24} /></span>
              {label}
            </Link>
          ))}
        </nav>

        {rest.length > 0 && (
          <section className="home__section">
            <div className="section-head">
              <div>
                <span className="eyebrow">Your visits</span>
                <h2 className="section-head__title">Coming up</h2>
              </div>
              <Link to={PATHS.appointments} className="section-head__link">See all</Link>
            </div>
            <div className="u-stack">
              {rest.slice(0, 3).map((a) => <AppointmentRow key={a.id} appointment={a} />)}
            </div>
          </section>
        )}

        {past.data.length > 0 && (
          <section className="home__section">
            <div className="section-head">
              <div>
                <span className="eyebrow">History</span>
                <h2 className="section-head__title">Recent visits</h2>
              </div>
              <Link to={PATHS.history} className="section-head__link">History</Link>
            </div>
            <div className="list-card">
              {past.data.slice(0, 3).map((a) => (
                <Link key={a.id} to={toPath(PATHS.appointmentDetail, { id: a.id })} className="list-row">
                  <div className="list-row__body">
                    <p className="list-row__title">{a.doctor?.name}</p>
                    <p className="list-row__sub">{`${a.doctor?.specialty} · ${formatShortDate(a.date)}`}</p>
                  </div>
                  <IconChevronRight size={20} className="list-row__chevron" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
